"use client";

import { Check, Crown, Star, Zap } from "lucide-react";
import { useMemo } from "react";
import { useTranslations } from "next-intl";
import type { Pack } from "./useBillingPacks";

type PackCard = Pack & {
	priceLabel: string;
	isPopular: boolean;
	Icon: typeof Zap;
};

type Props = {
	packs: Pack[];
	isLoading?: boolean;
	onSelect: (pack: Pack) => void;
	isProcessingPackId?: number | null;
	ctaLabel?: (pack: Pack, idx: number, isPopular: boolean) => string;
	variant?: "billing" | "landing";
};

export default function PacksGrid({
	packs,
	isLoading = false,
	onSelect,
	isProcessingPackId = null,
	ctaLabel,
	variant = "billing",
}: Props) {
	const t = useTranslations("BillingTokens");

	const packCards = useMemo(() => {
		const icons = [Zap, Star, Crown];
		return packs.map((pack, idx) => {
			const Icon = icons[idx % icons.length];
			const priceLabel = `${pack.price}₽`;
			const isPopular = idx === 1;
			return { ...pack, Icon, priceLabel, isPopular };
		});
	}, [packs]);

	const buildDetails = (pack: Pack) => {
		const generationsCount = typeof pack.amount === "number" ? Math.floor(pack.amount / 2) : 0;
		return [{ text: `${generationsCount} ${t("generations")} *`, tone: "success" }];
	};

	const cardClass = (isPopular: boolean) =>
		isPopular
			? "glass-card p-8 pt-12 rounded-[2rem] border border-primary/50 shadow-[0_0_30px_rgba(10,132,255,0.15)] relative flex flex-col transform md:-translate-y-4 transition-all duration-300 hover:shadow-[0_0_40px_rgba(10,132,255,0.25)]"
			: "glass-card p-8 pt-12 rounded-[2rem] border border-white/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col hover:border-white/10";

	const badgeClass = () =>
		"absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-primary text-white px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wider shadow-[0_4px_20px_rgba(10,132,255,0.4)] text-center whitespace-nowrap";

	const buttonClass = (isPopular: boolean) =>
		isPopular
			? "w-full py-4 text-center bg-primary text-white rounded-xl font-bold hover:bg-primary/90 shadow-[0_4px_14px_0_rgba(10,132,255,0.39)] hover:shadow-[0_6px_20px_rgba(10,132,255,0.23)] transition-all active:opacity-70 active:scale-[0.98]"
			: "w-full py-4 text-center border border-white/10 bg-secondary/30 text-foreground hover:bg-secondary/50 rounded-xl font-bold transition-all active:opacity-70 active:scale-[0.98]";

	return (
		<>
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4 mt-12 px-4 md:px-0">
				{isLoading && (
					<div className="col-span-full text-sm text-muted-foreground animate-pulse">
						{t("loading", { fallback: "Loading..." })}
					</div>
				)}
				{packCards.map((pack, idx) => {
					const buttonLabel = ctaLabel ? ctaLabel(pack, idx, pack.isPopular) : t("buy_pack");
					const details = buildDetails(pack);
					const isPopular = pack.isPopular;

					return (
						<div
							key={pack.pack_id ?? `${pack.name}-${idx}`}
							className={cardClass(isPopular)}
						>
							{isPopular && (
								<div className={badgeClass()}>
									{t("popular_choice")}
								</div>
							)}
							<div className="mb-6">
								<div
									className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isPopular
										? "bg-primary/20 text-primary"
										: "bg-white/5 text-muted-foreground"
										}`}
								>
									<pack.Icon size={24} />
								</div>
								<h3 className="text-foreground text-xl font-bold font-sans tracking-wide">{pack.name}</h3>
								<div className="flex items-baseline gap-1 mt-2">
									<span className="text-3xl font-bold text-foreground tracking-tight">
										{pack.priceLabel}
									</span>
								</div>
								{typeof pack.amount !== "undefined" && (
									<p className="text-sm mt-2 text-primary font-medium tracking-wide">
										{pack.amount} {t("tokens_count")}
									</p>
								)}
							</div>
							<div className="flex-1 border-t border-white/10 pt-6 mb-8">
								<ul className="space-y-4">
									{details.map((detail, detailIdx) => (
										<li key={detailIdx} className="flex items-start gap-3 text-muted-foreground">
											<Check size={20} className="text-primary mt-0.5 shrink-0" />
											<span className="font-medium">{detail.text}</span>
										</li>
									))}
								</ul>
							</div>
							<button
								type="button"
								onClick={() => onSelect(pack)}
								disabled={isProcessingPackId === pack.pack_id}
								className={buttonClass(isPopular)}
							>
								{isProcessingPackId === pack.pack_id ? t("loading", { fallback: "Loading..." }) : buttonLabel}
							</button>
						</div>
					);
				})}
			</div>
			<p className="px-4 md:px-0 mb-12 text-sm text-muted-foreground">{t("generations_footnote")}</p>
		</>
	);
}
